import { debug } from "../../utils"
import * as Types from "./api.types"
import { Client } from "./client"
// import { Alert } from "~/ui/primitives"
import { globalToast } from "~utils"

/**
 * Manages all requests to the API.
 */

export class APIService {
  client = new Client()
  baseUrl: string

  constructor(baseUrl: string) {
    debug(`new APIService ${baseUrl}`)
    this.baseUrl = baseUrl
  }

  url(path: string) {
    return `${this.baseUrl}${path}`
  }

  // 登录
  async login(data: Types.LoginParams): Promise<Types.AuthResult> {
    return await this.client.json<Types.AuthResult>(this.url(`/auth/login`), data)
  }

  // 注册
  async register(data: Types.RegisterParams): Promise<Types.AuthResult> {
    if (data.phone) {
      return await this.client.json<Types.AuthResult>(this.url(`/auth/phone_register`), data)
    } else {
      return await this.client.json<Types.AuthResult>(this.url(`/auth/email_register`), data)
    }
  }

  // 登出
  async logout(data: Types.LogoutParams): Promise<Types.LogoutResult> {
    return await this.client.post(this.url(`/auth/logout`), data)
  }

  // 忘记密码
  async forgetPassword(data: Types.ForgetPasswordParams): Promise<any> {
    return await this.client.post<any>(this.url(`/auth/pwd_code`), data)
  }

  // 重置密码
  async resetPassword(data: Types.ResetPasswordParams): Promise<any> {
    return await this.client.post<any>(this.url(`/auth/reset/pwd`), data)
  }

  // 修改密码
  async revisePassword(data: Types.RevisePasswordParams): Promise<any> {
    return await this.client.postWithAuth<any>(this.url(`/auth/new_reset/pwd`), data.token, data)
  }

  // 上传
  async upload(data: Types.UploadParams): Promise<Types.UploadResult> {
    return await this.client.upload(this.url("/api/uploads/file"), data)
  }

  // 上传
  async uploadClient(data: Types.UploadClientParams): Promise<Types.UploadClientResult> {
    return await this.client.get<Types.UploadClientResult>(
      this.url(`/upload/client/payload?filename=${data.filename}`),
    )
  }

  async uploadToS3Storage(
    s3url: Types.UploadClientResult["url"],
    formData: Types.UploadToS3StorageParams,
    onProgressUpdate: (progress: number) => void,
  ): Promise<Types.UploadToS3StorageResult> {
    return await this.client.uploadToS3Storage<Types.UploadToS3StorageResult>(
      s3url,
      formData,
      onProgressUpdate,
    )
  }

  // 下载
  async download(data: Types.DownloadParams) {
    return await this.client.download(data)
  }

  // 确认是否有新版本
  async checkVersion(data: Types.CheckVersionParams): Promise<Types.CheckVersionResult> {
    return await this.client.get<Types.CheckVersionResult>(
      this.url(`/app_versions/checkingVersion?version=${data.version}&device=${data.device}`),
    )
  }

  // 抓銀行列表
  async getBankList(): Promise<Types.CheckVersionResult> {
    return await this.client.get<Types.CheckVersionResult>(this.url(`/config/all`))
  }

  // 用户信息
  async getWalletInfo(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.postWithAuth<Types.GetResult>(this.url(`/api/users/i/info`), data.token, {})
  }

  // 錢包詳情
  async getWalletLog(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/wallet/logs`),
      data.token,
      data.params,
    )
  }

  async getForceCs(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/config/cs`),
      data.token,
      data.params,
    )
  }

  // 订单列表
  async getTakeOrders(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/order/take_orders`),
      data.token,
      data.params,
    )
  }

  // 订单列表-我賣出的
  async getTakeOrdersSold(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/order/take_orders/sold`),
      data.token,
      data.params,
    )
  }

  // 申诉列表
  async getTakeOrdersDisputed(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/order/take_orders/disputed`),
      data.token,
      data.params,
    )
  }

  // 支付列表
  async getPaymentList(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/user/payment/list`),
      data.token,
    )
  }

  // 掛單列表
  async getPendingOrders(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/order/pending_orders`),
      data.token,
    )
  }

  // 我的掛單列表
  async getMyOrders(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/order/my/sold?filters={"is_settled": ${data.isSettled}}`),
      data.token,
    )
  }

  // 我的掛單详情
  async getMyOrderDetail(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/order/pending_orders_details/${data.id}`),
      data.token,
    )
  }

  // 刪除支付
  async deletePayment(data: Types.DeletePayParams): Promise<Types.GetResult> {
    return await this.client.deleteWithAuth<Types.GetResult>(
      this.url(`/user/payment/delete`),
      data.token,
      data,
    )
  }

  // 实名认证
  async verify(data: Types.VerifyParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/verify`),
      data.token,
      data,
    )
  }

  // 手机短信验证
  async phoneVerify(data: Types.PhoneSmsParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/phone/verify`),
      data.token,
      data,
    )
  }

  // 短信验证码
  async phoneSms(data: Types.PhoneSmsParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/phone/sms/code`),
      data.token,
      data,
    )
  }

  async phoneSmsv1(data: Types.PhoneSmsParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/sms/code/v1`),
      data.token,
      data,
    )
  }

  // 发送手机验证码(未绑定手机)
  async sendPhoneCodeByNotBound(data: Types.PhoneSmsParams): Promise<Types.PostResult> {
    return await this.client.post<Types.PostResult>(
      this.url(`/api/efront/send/phone`),
      data,
    )
  }

  // 创建支付密码
  async paypws(data: Types.CreatePayParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/pay_pwd/create`),
      data.token,
      data,
    )
  }

  // 驗證支付密码
  async payCheck(data: Types.CheckPayParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/pay_pwd/check`),
      data.token,
      data,
    )
  }

  // 更新支付密码
  async updatePaypws(data: Types.UpdatePayPwdParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/pay_pwd/update`),
      data.token,
      data,
    )
  }

  // 创建支付
  async createPay(data: Types.CreatePayParams, token: string): Promise<Types.PostResult> {
    return await this.client.createPay<Types.PostResult>(
      this.url(`/user/payment/create`),
      token,
      data,
    )
  }

  // 匯率
  async getExchangeRate(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(this.url(`/exchange/conf`), data.token)
  }

  // 兌換清單
  async getExchangeList(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(this.url(`/exchange/list`), data.token)
  }

  // 取消兌換
  async cancelExchange(data: Types.CancelExchangeParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/exchange/cancel`),
      data.token,
      data,
    )
  }

  // 建立兌換
  async createExchange(data: Types.CreateExchangeParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/exchange/create`),
      data.token,
      data,
    )
  }

  // 申诉列表
  async getComplaintList(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(this.url(`/exchange/list`), data.token)
  }

  // 提交申诉
  async saveComplaint(data: Types.SaveComplaintParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/exchange/create`),
      data.token,
      data,
    )
  }

  // 信用及评价(我的/好友)
  async getMyCredit(data: Types.MyCreditParams, token: string): Promise<Types.GetResult> {
    const dataStr = JSON.stringify(data)
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/evaluation/list?filters=${dataStr}`),
      token,
    )
  }

  // 信用评分规则
  async getCreditRule(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/evaluation/get/credit_score_rule`),
      data.token,
    )
  }

  // 评分规则
  async getRatingRule(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/evaluation/get/order_rule`),
      data.token,
    )
  }

  // 提交订单评价
  async saveCredit(data: Types.SaveCreditParams, token: string): Promise<Types.PostResult> {
    return await this.client.postFormDataWithAuth<Types.PostResult>(
      this.url(`/evaluation/add`),
      token,
      data,
    )
  }

  // 获取邮箱验证码
  async emailSms(data: Types.EmailSmsParams): Promise<Types.PostResult> {
    return await this.client.post<Types.PostResult>(
      this.url(`/api/efront/send/email`),
      data,
    )
  }
  // 提交修改邮箱
  async updateEmail(data: Types.UpdateEmailParams, token: string): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/user/update_email`),
      token,
      data,
    )
  }
  // 获取我加入的频道/群
  async getJoinChatGroupAndChannelList(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.postWithAuth<Types.GetResult>(
      // this.url(`/api/chats/list`),
      this.url(`/api/chats/list/by_type?type=${data.params.type}&page=${data.params.page??1}&limit=${data.params.limit??10}`),
      data.token,
      data.params
    )
  }

  
  // 获取群组/频道设置信息
  async getChannelGroupSettingInfo(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuth<Types.GetResult>(
      this.url(`/api/chats/settings/${data.params.chat_id}?type=${data.params.type}`),
      data.token
    )
  }

  // 更新群组设置
  async updateChannelGroupSetting(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/api/chats/settings/group/${data.params.chat_id}`),
      data.token,
      data.params
    )
  }

  // 找回密码
  async findPassword(data: Types.FindPasswordParams): Promise<Types.PostResult> {
    return await this.client.post<Types.PostResult>(
      this.url(`/auth/verify_phone_email`),
      data,
    )
  }

  // 重置密码
  async updatePassword(data: Types.UpdatePasswordParams): Promise<Types.PostResult> {
    return await this.client.post<Types.PostResult>(
      this.url(`/auth/up_password`),
      data,
    )
  }

  // 获取群聊数据统计
  async getGroupChatDataStatistics(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuthParams<Types.GetResult>(
      this.url(`/api/chat_reports/daily/${data.params.chat_id}`),
      data.token,
      data.params
    )
  }

  // 获取群聊数据列表
  async getGroupChatDataList(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuthParams<Types.GetResult>(
      this.url(`/api/chats/members/daily_reports/${data.params.chat_id}`),
      data.token,
      data.params
    )
  }

  // 获取账户信息
  async getBindAccountInfo(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.postWithAuth<Types.GetResult>(
      this.url(`/api/cards/find_card`),
      data.token,
      {}
    )
  }

  // 获取当前用户的通知列表
  async getUserNotificationList(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuthParams<Types.GetResult>(
      this.url(`/api/new_notices/`),
      data.token,
      data.params
    )
  }

  // 获取当前用户未读通知数量
  async getUserUnreadNotificationCount(data: Types.GetParams): Promise<Types.GetResult> {
    return await this.client.getWithAuthParams<Types.GetResult>(
      this.url(`/api/new_notices/unread_count`),
      data.token,
      data.params
    )
  }

  // 标记通知为已读
  async markNotificationAsRead(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/api/new_notices/read/${data.params.id}`),
      data.token,
      data.params
    )
  }

  // 标记当前用户所有通知为已读
  async markAllNotificationsAsRead(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/api/new_notices/read_all`),
      data.token,
      data.params
    )
  }

  // 标记全部已读后重新拉取通知中心列表
  async markAllNotificationsAsReadAndRefreshList(
    data: Types.GetParams,
    listParams: Record<string, string | number> = { page: 1, limit: 20 },
  ): Promise<{ read: Types.PostResult; list: Types.GetResult }> {
    const read = await this.markAllNotificationsAsRead(data)
    const list = await this.getUserNotificationList({
      token: data.token,
      params: listParams,
      isSettled: data.isSettled ?? false,
    })
    return { read, list }
  }

  // 获取聊天室ID
  async getChatRoomId(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/api/chats/private`),
      data.token,
      data.params
    )
  }
  // 获取红包详情
  async getRedpacketDetail(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/api/chats/redpacket/detail`),
      data.token,
      data.params
    )
  }
  // 获取转账详情
  async getTransferDetail(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/api/chats/transfer/detail`),
      data.token,
      data.params
    )
  }

  // 获取个人设置中心
  async setPersonalCenterSetting(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/api/users/i/setting`),
      data.token,
      data.params
    )
  }

  // 根据 chat_id + user_id 查询群成员的自动发包配置
  async getAutoSendRedpacketConfig(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/api/chats/auto_redpacket/get`),
      data.token,
      data.params
    )
  }
  // 录入/更新群成员的自动发包配置
  async saveAutoSendRedpacketConfig(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/api/chats/auto_redpacket/upsert`),
      data.token,
      data.params
    )
  }
  // 查询当前用户的谷歌验证码绑定信息
  async getGoogleAuthInfo(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.getWithAuth<Types.PostResult>(
      this.url(`/api/users/i/google_auth`),
      data.token,
      data.params
    )
  }

  // 绑定谷歌验证码
  async bindGoogleAuthCode(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/api/users/i/google_auth/bind`),
      data.token,
      data.params
    )
  }

  // 用户自助开启或关闭 2FA 谷歌验证
  async toggleGoogleAuth(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/api/users/i/google_auth/toggle`),
      data.token,
      data.params
    )
  }
  // 当前用户所有未注销的登录设备
  async getLoginDevices(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.getWithAuth<Types.PostResult>(
      this.url(`/api/users/i/devices`),
      data.token,
      data.params
    )
  }
  // 注销指定 device_id 的登录设备
  async logoutLoginDevice(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/api/users/i/devices/revoke`),
      data.token,
      data.params
    )
  }
  // 注销除当前请求设备外的全部登录设备
  async logoutAllLoginDevices(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/api/users/i/devices/revoke_others`),
      data.token,
      data.params
    )
  }

  // 双重验证
  async verifyGoogleAuthLogin(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.post<Types.PostResult>(
      this.url(`/auth/login/verify_2fa`),
      data
    )
  }

  // 新设备登录验证
  async verifyNewDeviceLogin(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.post<Types.PostResult>(
      this.url(`/auth/login/verify_device`),
      data
    )
  }

  // 查询消息自毁设置
  async getMessageDestroySetting(data: Types.GetParams): Promise<Types.PostResult> {
    return await this.client.postWithAuth<Types.PostResult>(
      this.url(`/api/users/i/msg_destruct`),
      data.token,
      data.params
    )
  }
}
